import type { LoadedPolicy } from '@open-policy-agent/opa-wasm'

import {
  fetchAndLoadPolicyAssetsFromTarGz,
  loadPolicyAssetsFromStaticPublic,
  DEFAULT_GATEKEEPER_BUNDLE_URL,
} from './policyBundleLoader'

/** Policy resource action (OPA input `resource.action`). */
export type ResourceAction = 'view' | 'create' | 'update' | 'delete'

/** @deprecated Use `ResourceAction` and scope operations `view` | `create` | `update` | `delete`. */
export type PermissionAction = 'read' | 'write'

export type PolicyLoadStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface PermissionResource {
  cloud: string
  service: string
}

/** Second argument to `canPerformAction`: resource plus action (OPA `resource.action`). */
export interface PermissionResourceWithAction {
  cloud: string
  service: string
  action: ResourceAction | 'read' | 'write'
}

export interface PermissionInput {
  resource: PermissionResource
  action: PermissionAction
}

export interface PermissionRequirement {
  key?: string
  cloud: string
  service: string
  /** Prefer `view` | `create` | `update` | `delete`. Legacy `read`/`write` are mapped. */
  action: ResourceAction | PermissionAction
}

export interface GatekeeperPolicyAssets {
  wasm: ArrayBuffer
  manifest: Record<string, unknown>
  data: Record<string, unknown>
  rego: string | null
  loadedAt: number
  /** Instantiated OPA WASM policy; set after successful `loadPolicy` during preload. */
  opaPolicy?: LoadedPolicy
}

export interface GatekeeperPolicyInput {
  scopes: string[]
  resource: {
    cloud: string
    service: string
    action: string
  }
}

const LEGACY_READ_WRITE_TO_ACTION: Record<PermissionAction, ResourceAction> = {
  read: 'view',
  write: 'update',
}

export const mapLegacyPermissionAction = (
  action: PermissionAction
): ResourceAction => LEGACY_READ_WRITE_TO_ACTION[action]

export const normalizeRequirementAction = (
  action: ResourceAction | PermissionAction
): ResourceAction =>
  action === 'read' || action === 'write'
    ? mapLegacyPermissionAction(action)
    : action

const POLICY_BUNDLE_URL =
  import.meta.env.VITE_GATEKEEPER_BUNDLE_URL ?? DEFAULT_GATEKEEPER_BUNDLE_URL

/** Load compiled policy from `public/opa/gatekeeper` or from the remote bundle URL. */
export type GatekeeperPolicySource = 'local' | 's3'

export function resolveGatekeeperPolicySource(): GatekeeperPolicySource {
  const raw = import.meta.env.VITE_GATEKEEPER_POLICY_SOURCE
  if (raw === 'local' || raw === 's3') {
    return raw
  }
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
    return 'local'
  }
  return 's3'
}

const wasmEntrypointFromManifest = (
  manifest: Record<string, unknown>
): string | undefined => {
  const wasm = manifest.wasm as Array<{ entrypoint?: string }> | undefined
  return wasm?.[0]?.entrypoint
}

export const toGatekeeperPolicyInput = (
  scopes: string[],
  resource: PermissionResource,
  action: ResourceAction
): GatekeeperPolicyInput => ({
  scopes,
  resource: {
    cloud: resource.cloud,
    service: resource.service,
    action,
  },
})

/**
 * Authorization check via loaded OPA WASM only. If the policy bundle is missing or WASM
 * failed to instantiate, denies and logs a warning (no client-side scope fallback).
 */
export const canDo = (
  policy: GatekeeperPolicyAssets | null,
  scopes: string[],
  resource: PermissionResource,
  action: ResourceAction
): boolean => {
  if (!policy?.opaPolicy) {
    // eslint-disable-next-line no-console
    console.warn('Authorization denied: OPA WASM policy is not loaded')
    return false
  }

  const input = toGatekeeperPolicyInput(scopes, resource, action)
  const entrypoint =
    wasmEntrypointFromManifest(policy.manifest) ?? 'gatekeeper/allow'
  try {
    const resultSet = policy.opaPolicy.evaluate(input, entrypoint)
    if (resultSet && resultSet.length > 0) {
      return resultSet[0].result === true
    }
    return false
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('OPA policy evaluation failed', err)
    return false
  }
}

class GatekeeperPolicyEngine {
  #cachedPolicy: GatekeeperPolicyAssets | null = null
  #loadPromise: Promise<GatekeeperPolicyAssets> | null = null
  #status: PolicyLoadStatus = 'idle'
  #error: string | null = null

  getStatus(): PolicyLoadStatus {
    return this.#status
  }

  getError(): string | null {
    return this.#error
  }

  getCachedPolicy(): GatekeeperPolicyAssets | null {
    return this.#cachedPolicy
  }

  getPolicySource(): GatekeeperPolicySource {
    return resolveGatekeeperPolicySource()
  }

  isPolicyLoaded(): boolean {
    return Boolean(this.#cachedPolicy)
  }

  async preloadPolicy(): Promise<GatekeeperPolicyAssets> {
    if (this.#cachedPolicy) return this.#cachedPolicy
    if (this.#loadPromise) return this.#loadPromise

    this.#status = 'loading'
    this.#error = null

    this.#loadPromise = this.#loadPolicyAssets()
      .then(async policy => {
        try {
          const { loadPolicy } = await import('@open-policy-agent/opa-wasm')
          const opaPolicy = await loadPolicy(policy.wasm)
          if (policy.data) {
            opaPolicy.setData(policy.data)
          }
          policy.opaPolicy = opaPolicy
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to load OPA WASM policy', err)
        }

        this.#cachedPolicy = policy
        this.#status = 'ready'
        return policy
      })
      .catch(error => {
        this.#status = 'error'
        this.#error = error instanceof Error ? error.message : String(error)
        throw error
      })
      .finally(() => {
        this.#loadPromise = null
      })

    return this.#loadPromise
  }

  canPerformAction(
    scopes: string[],
    resource: PermissionResourceWithAction
  ): boolean {
    const { cloud, service, action } = resource
    const normalized =
      action === 'read' || action === 'write'
        ? mapLegacyPermissionAction(action)
        : action
    return canDo(this.#cachedPolicy, scopes, { cloud, service }, normalized)
  }

  evaluateManyPermissions(
    scopes: string[],
    permissions: PermissionInput[]
  ): boolean[] {
    return permissions.map(({ resource, action }) =>
      this.canPerformAction(scopes, { ...resource, action })
    )
  }

  canAccess(scopes: string[], requirement: PermissionRequirement): boolean {
    const normalized = normalizeRequirementAction(requirement.action)
    return this.canPerformAction(scopes, {
      cloud: requirement.cloud,
      service: requirement.service,
      action: normalized,
    })
  }

  evaluatePermissions(
    scopes: string[],
    requirements: PermissionRequirement[]
  ): Record<string, boolean> {
    return requirements.reduce<Record<string, boolean>>((acc, requirement) => {
      const normalized = normalizeRequirementAction(requirement.action)
      const key =
        requirement.key ||
        `${requirement.cloud}:${requirement.service}:${normalized}`
      acc[key] = this.canAccess(scopes, requirement)
      return acc
    }, {})
  }

  async #loadPolicyAssets(): Promise<GatekeeperPolicyAssets> {
    const source = resolveGatekeeperPolicySource()
    if (source === 'local') {
      return loadPolicyAssetsFromStaticPublic()
    }
    return fetchAndLoadPolicyAssetsFromTarGz(POLICY_BUNDLE_URL)
  }

  // Test helper to isolate singleton state between specs.
  __resetForTests(): void {
    this.#cachedPolicy = null
    this.#loadPromise = null
    this.#status = 'idle'
    this.#error = null
  }

  /** Test helper: seed cached assets (e.g. mock `opaPolicy`) without loading WASM. */
  __setCachedPolicyForTests(assets: GatekeeperPolicyAssets | null): void {
    this.#cachedPolicy = assets
    this.#status = assets ? 'ready' : 'idle'
    this.#error = null
  }
}

export const gatekeeperPolicyEngine = new GatekeeperPolicyEngine()

export const preloadAuthorizationPolicy = async (): Promise<void> => {
  await gatekeeperPolicyEngine.preloadPolicy()
}

export const canPerformAction = (
  scopes: string[],
  resource: PermissionResourceWithAction
): boolean => gatekeeperPolicyEngine.canPerformAction(scopes, resource)

export const evaluateManyPermissions = (
  scopes: string[],
  permissions: PermissionInput[]
): boolean[] =>
  gatekeeperPolicyEngine.evaluateManyPermissions(scopes, permissions)

export const getPolicyLoadStatus = (): PolicyLoadStatus =>
  gatekeeperPolicyEngine.getStatus()
export const getPolicyLoadError = (): string | null =>
  gatekeeperPolicyEngine.getError()
export const getCachedPolicyAssets = (): GatekeeperPolicyAssets | null =>
  gatekeeperPolicyEngine.getCachedPolicy()

export const getGatekeeperPolicySource = (): GatekeeperPolicySource =>
  gatekeeperPolicyEngine.getPolicySource()

export const resetPolicyEngineForTests = (): void =>
  gatekeeperPolicyEngine.__resetForTests()

export const setCachedPolicyForTests = (
  assets: GatekeeperPolicyAssets | null
): void => gatekeeperPolicyEngine.__setCachedPolicyForTests(assets)
